// src/encounter/lib/patient-problems.ts
// FOUR-MOMENTS data layer (PROD-1656 spec §10, mounting wave 2026-07-24) — PURE helpers that
// connect the visit's A&P diagnoses to the patient's chart problem list:
//   • toPatientProblems       — chart-detail Active conditions → the lite shape the picker/cards read
//   • matchProblem            — day-1 on-list identity = CODE-STRING equality (honest caption, spec
//                               §10.2: upgrades to record identity when the memory column PROD-1684
//                               lands — the UI is unchanged by that upgrade)
//   • buildDxSearchGroups     — the patient-aware picker groups (M2): the patient's own matching
//                               active problems ALWAYS first (badged), catalogue below (on-list
//                               codes deduped out of the catalogue group)
//   • buildUnresolveValue     — the ConditionFormValue that walks a resolve back (undo/reactivate):
//                               restores Active + the ORIGINAL onset (never resets it — this is an
//                               undo, not the chart tab's deliberate unresolve-with-new-onset)
// PURE — no I/O.
import type { CodeSearchGroup, CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { PatientCondition } from '@/types/patient-chart-api';
import { normalizeConditionCode } from '@/patients/lib/conditions';
import type { ConditionFormValue } from './to-condition-request';

/** The lite active-problem row the four-moments surfaces read (identity = code string, v1). */
export interface PatientProblemLite {
  /** Chart Condition identifier (the record the picker must pull — never a duplicate). */
  identifier: string;
  /** ICD-10 code string — the day-1 identity key (spec §10.2 honesty note). */
  code: string;
  display: string;
  /** Onset year for the "since YYYY" badge; absent when onset is unknown. */
  sinceYear?: number;
}

/** Chart-detail Active conditions → the lite shape. Deleted rows and code-less rows drop. */
export function toPatientProblems(conditions: PatientCondition[] | undefined): PatientProblemLite[] {
  return (conditions ?? [])
    .filter((c) => c.isDeleted !== true && !!c.identifier && !!c.code?.code)
    .map((c) => {
      const year = onsetYear(c.onsetDateTime);
      return {
        identifier: c.identifier as string,
        code: (c.code?.code as string).trim(),
        display: c.code?.display?.trim() || 'Problem',
        ...(year != null ? { sinceYear: year } : {}),
      };
    });
}

/** Parse the onset year from an ISO onset (null-safe, NaN-safe). */
export function onsetYear(iso: string | null | undefined): number | undefined {
  if (!iso || iso === 'n/a') return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.getFullYear();
}

/** Day-1 on-list match: code-string equality, case-insensitive (spec §10.2). */
export function matchProblem(
  problems: PatientProblemLite[] | undefined,
  code: string | null | undefined,
): PatientProblemLite | undefined {
  const key = code?.trim().toLowerCase();
  if (!key) return undefined;
  return (problems ?? []).find((p) => p.code.toLowerCase() === key);
}

/**
 * An active problem as the picker's result. The Condition identifier IS the result id — the add path then keys the
 * visit dx by the chart record (same record, never a duplicate; record identity proper lands with PROD-1684).
 * Shared by the A/P picker's "Patient's active problems" group and the chart column's "Add to note".
 */
export function problemToCodeSearchResult(p: PatientProblemLite): CodeSearchResult {
  return { id: p.identifier, code: p.code, display: p.display, system: 'ICD-10' };
}

/** The identity badge the picker rows + result groups carry (pronoun-neutral register). */
export function problemBadge(p: PatientProblemLite): string {
  return p.sinceYear ? `On problem list · since ${p.sinceYear}` : 'On problem list';
}

/**
 * The patient-aware picker groups (M2, spec §10.2): the patient's own active problems matching the
 * query form the FIRST group (badged); the catalogue results follow, minus any code already in the
 * first group (selecting an on-list result must pull the same record — never a duplicate).
 * Empty query → no groups (the CONSUMER owns clearing groups when the query empties — DS contract).
 */
export function buildDxSearchGroups(
  query: string,
  catalogue: CodeSearchResult[],
  problems: PatientProblemLite[] | undefined,
): CodeSearchGroup[] | undefined {
  const q = query.trim().toLowerCase();
  if (!q) return undefined;
  const mine = (problems ?? []).filter(
    (p) => p.display.toLowerCase().includes(q) || p.code.toLowerCase().startsWith(q),
  );
  const mineCodes = new Set(mine.map((p) => p.code.toLowerCase()));
  const mineResults: CodeSearchResult[] = mine.map((p) => ({ ...problemToCodeSearchResult(p), badge: problemBadge(p) }));
  const rest = catalogue.filter((r) => !r.code || !mineCodes.has(r.code.toLowerCase()));
  const groups: CodeSearchGroup[] = [];
  if (mineResults.length > 0) groups.push({ label: "Patient's active problems", results: mineResults });
  groups.push({ label: 'Catalogue', results: rest });
  return groups;
}

/**
 * The edit-form value that walks a resolve back (the resolve-with-visit UNDO, and the wrap-up
 * "Reactivate"): clinicalStatus → Active, keeping the ORIGINAL onset + note (severity is kept by the update body itself). Distinct
 * from the chart tab's unresolve (which deliberately resets onset to now — a re-activation, not an
 * undo). buildConditionRequest('edit') round-trips every other field from `existing`.
 */
export function buildUnresolveValue(existing: PatientCondition): ConditionFormValue {
  return {
    // normalizeConditionCode is null for a code-less record — fall back to a minimal coding so the
    // walk-back never drops the term (the resolve path only runs on coded, matched conditions).
    code:
      normalizeConditionCode(existing.code) ?? {
        code: existing.code?.code ?? null,
        Definition: null,
        display: existing.code?.display ?? null,
        system: existing.code?.system ?? null,
        description: null,
        shortName: null,
      },
    clinicalStatus: 'Active',
    onsetDateTime: existing.onsetDateTime ?? null,
    onsetDisplayDateTime: existing.onsetDisplayDateTime ?? null,
    note: existing.note ?? null,
    // No severity here: the form value no longer carries one (ruling 1 Nov 2026), and the update
    // body round-trips the record's stored severity on every path, so it is still kept.
  };
}

/** Local-calendar today as YYYY-MM-DD (the PROD-1489 local-date rule — never UTC-sliced). */
export function todayLocalYmd(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** True when an ISO date falls on the local-calendar TODAY (resolved-today wrap-up group). */
export function isTodayLocal(iso: string | null | undefined, now = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}
